/**
 * Firebase Authentication UI Component
 * Provides email/password and Google sign-in
 */

import { useState, useEffect, useRef } from "react";
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  type Auth,
  type User,
} from "firebase/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFunnelTracking } from "@/hooks/useFunnelTracking";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, Lock, AlertCircle } from "lucide-react";
import { getTrpcUrl } from "@/lib/api-config";

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

/**
 * Initialize Firebase with config from environment
 */
function initializeFirebase(): Auth | null {
  if (firebaseAuth) {
    return firebaseAuth;
  }

  const config: FirebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  };

  // Check if Firebase is configured
  if (!config.apiKey || !config.authDomain || !config.projectId) {
    console.log("[Firebase] Not configured - missing environment variables");
    return null;
  }

  try {
    // Use existing app if already initialized (e.g., by main.tsx)
    firebaseApp = getApps().length === 0 ? initializeApp(config) : getApps()[0];
    firebaseAuth = getAuth(firebaseApp);
    console.log("[Firebase] Initialized successfully");
    return firebaseAuth;
  } catch (error) {
    console.error("[Firebase] Initialization failed:", error);
    return null;
  }
}

interface FirebaseAuthProps {
  onSuccess?: (user: User) => void;
  onError?: (error: Error) => void;
}

export function FirebaseAuth({ onSuccess, onError }: FirebaseAuthProps) {
  const [auth, setAuth] = useState<Auth | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const { trackEvent } = useFunnelTracking();
  const signupTrackedRef = useRef(false);

  useEffect(() => {
    const firebaseAuth = initializeFirebase();
    setAuth(firebaseAuth);
  }, []);

  // If Firebase not configured, show error
  if (!auth) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Authentication Unavailable</CardTitle>
          <CardDescription>
            Firebase Authentication could not be initialized. Please check the
            console for errors or contact support if the issue persists.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Authentication service is currently unavailable. Please try
              refreshing the page.
            </AlertDescription>
          </Alert>
          <Button
            className="w-full mt-4"
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let userCredential;
      if (mode === "signin") {
        userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
      } else {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
      }

      // Trigger user creation via tRPC auth.me (replaces legacy /firebase/verify)
      const idToken = await userCredential.user.getIdToken();
      const response = await fetch(
        `${getTrpcUrl()}/auth.me?batch=1&input=%7B%7D`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (!response.ok) {
        throw new Error("Failed to authenticate");
      }

      if (mode === "signup") {
        trackEvent("signup_complete", { method: "email" });
      }

      onSuccess?.(userCredential.user);
    } catch (err: any) {
      const errorMessage = err.code
        ? getFirebaseErrorMessage(err.code)
        : err.message || "Authentication failed";
      setError(errorMessage);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      // Validate auth domain configuration to prevent invalid-continue-uri
      const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
      const origin = window.location.origin;
      console.log("[Firebase] Google Sign-In attempt", {
        authDomain,
        origin,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      });

      if (!authDomain) {
        throw new Error(
          "Firebase Auth domain is not configured. Please set VITE_FIREBASE_AUTH_DOMAIN to your Firebase project's authorized domain."
        );
      }
      // Basic check: if current origin isn't included in authDomain (custom domains often match), warn early
      if (!authDomain.includes(new URL(origin).hostname)) {
        console.warn(
          "[Firebase] Current origin doesn't match configured authDomain",
          { authDomain, origin }
        );
      }

      const provider = new GoogleAuthProvider();
      console.log("[Firebase] Opening Google sign-in popup...");
      const userCredential = await signInWithPopup(auth, provider);
      console.log(
        "[Firebase] Google sign-in successful, user:",
        userCredential.user.email
      );

      // Trigger user creation via tRPC auth.me (replaces legacy /firebase/verify)
      const idToken = await userCredential.user.getIdToken();
      console.log(
        "[Firebase] Got ID token, triggering user session via auth.me..."
      );
      const response = await fetch(
        `${getTrpcUrl()}/auth.me?batch=1&input=%7B%7D`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "[Firebase] auth.me session failed:",
          response.status,
          errorText
        );
        throw new Error("Failed to authenticate");
      }

      console.log("[Firebase] auth.me session established");
      if (mode === "signup") {
        trackEvent("signup_complete", { method: "google" });
      }
      onSuccess?.(userCredential.user);
    } catch (err: any) {
      // Log the FULL error for debugging
      console.error("[Firebase] Google Sign-In Error:", {
        code: err.code,
        message: err.message,
        name: err.name,
        fullError: err,
      });

      const errorMessage = err.code
        ? getFirebaseErrorMessage(err.code)
        : err.message || "Google sign-in failed";
      setError(errorMessage);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Welcome</CardTitle>
        <CardDescription>
          Sign in to access the Unified Tax Platform
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={mode}
          onValueChange={v => {
            const newMode = v as "signin" | "signup";
            setMode(newMode);
            if (newMode === "signup" && !signupTrackedRef.current) {
              signupTrackedRef.current = true;
              trackEvent("signup_start", { method: "email" });
            }
          }}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="signin" className="space-y-4">
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="signup" className="space-y-4">
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Password must be at least 6 characters
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>

      <CardFooter className="flex flex-col space-y-4">
        <div className="relative w-full">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          )}
          Sign in with Google
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * Convert Firebase error codes to user-friendly messages
 */
function getFirebaseErrorMessage(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "This email is already registered. Please sign in instead.";
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/operation-not-allowed":
      return "Email/password sign-in is not enabled.";
    case "auth/weak-password":
      return "Password is too weak. Please use at least 6 characters.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
      return "Incorrect password.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed. Please try again.";
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled.";
    default:
      return "Authentication failed. Please try again.";
  }
}

/**
 * Hook to get current Firebase user
 */
export function useFirebaseAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = initializeFirebase();
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading };
}
